<script lang="ts">
  import { afterNavigate } from "$app/navigation";

  import { Hamburger } from "svelte-hamburgers";

  import lightCursor from "$lib/assets/marketing/gpu-glossary/light-cursor.svg";
  import lightGreenCursor from "$lib/assets/marketing/gpu-glossary/light-green-cursor.svg";
  import lightGreenPointerCursor from "$lib/assets/marketing/gpu-glossary/light-green-pointer-cursor.svg";
  import lightGreenSelectionCursor from "$lib/assets/marketing/gpu-glossary/light-green-selection-cursor.svg";
  import lightPointerCursor from "$lib/assets/marketing/gpu-glossary/light-pointer-cursor.svg";
  import lightSelectionCursor from "$lib/assets/marketing/gpu-glossary/light-selection-cursor.svg";
  import logoLightGreen from "$lib/assets/marketing/gpu-glossary/modal-logo-light-green.svg";
  import logoLight from "$lib/assets/marketing/gpu-glossary/modal-logo-light.svg";
  import logoTerminal from "$lib/assets/marketing/gpu-glossary/modal-logo-terminal.svg";
  import terminalCursor from "$lib/assets/marketing/gpu-glossary/terminal-cursor.svg";
  import terminalPointerCursor from "$lib/assets/marketing/gpu-glossary/terminal-pointer-cursor.svg";
  import terminalSelectionCursor from "$lib/assets/marketing/gpu-glossary/terminal-selection-cursor.svg";
  import type { ThemeName } from "$routes/(microsites)/gpu-glossary/_lib/gpu-glossary";
  import { themeStore } from "$routes/(microsites)/gpu-glossary/_lib/gpu-glossary";
  import { isGlossaryMobileMenuOpen } from "$routes/(microsites)/gpu-glossary/_lib/gpu-glossary";
  import Contents from "./_components/Contents.svelte";
  import GlossarySearch from "./_components/GlossarySearch.svelte";
  import SignUpButton from "./_components/SignUpButton.svelte";
  import ThemeOptions from "./_components/ThemeOptions.svelte";
  import contents from "./_components/contents.json";

  let scrollContainer: HTMLDivElement;

  const cursorMap: Record<ThemeName, string> = {
    lightGreen: lightGreenCursor,
    terminal: terminalCursor,
    light: lightCursor,
  };

  const pointerCursorMap: Record<ThemeName, string> = {
    lightGreen: lightGreenPointerCursor,
    terminal: terminalPointerCursor,
    light: lightPointerCursor,
  };

  const selectionCursorMap: Record<ThemeName, string> = {
    lightGreen: lightGreenSelectionCursor,
    terminal: terminalSelectionCursor,
    light: lightSelectionCursor,
  };

  $: cursorUrl = cursorMap[$themeStore.name];
  $: pointerCursorUrl = pointerCursorMap[$themeStore.name];
  $: selectionCursorUrl = selectionCursorMap[$themeStore.name];

  afterNavigate(() => {
    scrollContainer.scrollTop = 0;
  });
  $: style = `
  --cursor-url: url("${cursorUrl}");
  --pointer-cursor-url: url("${pointerCursorUrl}");
  --selection-color: ${$themeStore.primary[20]};
  --selection-cursor-url: url("${selectionCursorUrl}");
  `;
</script>

<svelte:head>
  <link rel="stylesheet" href="https://use.typekit.net/psj2ndn.css" />
</svelte:head>

<main
  class="custom-scrollbar flex h-screen w-screen md:p-4"
  style:background-color={$themeStore.secondary[100]}
  style:color={$themeStore.primary[100]}
  {style}
>
  <div
    class="glossary-font-primary custom-cursor flex w-full flex-col border"
    style:border-color={$themeStore.primary[100]}
    style:background-color={$themeStore.primary[5]}
  >
    <div
      class="flex items-center justify-between border-b pl-4 md:grid md:grid-cols-4 md:pl-0"
      style:border-color={$themeStore.primary[100]}
    >
      <a
        class="-ml-3 flex items-center gap-x-2 p-2 text-3xl leading-6 md:-ml-0 md:p-4"
        href="/"
        style:color={$themeStore.primary[100]}
      >
        {#if $themeStore.name === "light"}
          <img src={logoLight} alt="GPU Glossary" />
        {:else if $themeStore.name === "lightGreen"}
          <img src={logoLightGreen} alt="GPU Glossary" />
        {:else}
          <img src={logoTerminal} alt="GPU Glossary" />
        {/if}
        <div
          class="glossary-font-secondary col-span-2 flex items-center text-xl md:ml-14 md:hidden"
        >
          GPU Glossary
        </div>
      </a>
      <div
        class="glossary-font-secondary col-span-2 hidden items-center text-2xl md:ml-14 md:flex"
      >
        GPU Glossary
      </div>
      <div
        class="glossary-font-secondary hidden items-center justify-end gap-x-4 p-4 text-sm md:flex"
      >
        <div class="flex shrink-0 gap-x-4 md:hidden lg:flex">
          <ThemeOptions />
        </div>
        <div class="flex shrink-0 items-center gap-x-6 pl-3">
          <GlossarySearch />
          <SignUpButton />
        </div>
      </div>
      <div
        class="flex items-center gap-x-2 self-end px-2 py-4 md:hidden md:p-4"
      >
        <div class="block md:hidden">
          <GlossarySearch />
        </div>
        <Hamburger
          bind:open={$isGlossaryMobileMenuOpen}
          --color={$themeStore.primary[100]}
          --layer-height="1.5px"
          --layer-width="18px"
          --layer-spacing="5px"
          --padding="4px"
        />
      </div>
    </div>
    {#if $isGlossaryMobileMenuOpen}
      <Contents {contents} showAdditionalControls={true} />
    {/if}
    <div
      class="grid grid-cols-4"
      style="height: calc(100% - 56px);"
      style:display={$isGlossaryMobileMenuOpen ? "none" : "grid"}
    >
      <div
        class="background-styles hidden overflow-y-auto md:block"
        style:background-image={`linear-gradient(to bottom, ${$themeStore.primary[100]} 50%, transparent 50%)`}
        style:background-size="1px 12px"
        style:background-position="right"
        style:background-repeat="repeat-y"
        style:height="calc(100% - 5px)"
      >
        <Contents {contents} showAdditionalControls={false} />
      </div>
      <div
        class="relative col-span-4 overflow-y-auto md:col-span-3"
        style:background-color={$themeStore.secondary[100]}
        style="height: calc(100% - 5px)"
        bind:this={scrollContainer}
      >
        <slot />
      </div>
    </div>
  </div>
</main>

<style>
  main :global(.glossary-font-primary) {
    font-family: "degular-mono", sans-serif;
  }
  main :global(.glossary-font-secondary) {
    font-family: "cofo-sans-pixel", sans-serif;
  }

  main {
    cursor:
      var(--cursor-url) 12 12,
      auto;
  }

  main :global([style*="cursor: pointer"]),
  main :global(a),
  main :global(button) {
    cursor: var(--pointer-cursor-url), pointer;
  }

  @media (max-width: 640px) {
    .background-styles {
      background-image: none !important;
    }
  }
  main :global(::selection) {
    background-color: var(--selection-color);
  }

  main:active {
    cursor: var(--selection-cursor-url), text;
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: var(--selection-color) transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    width: 4px;
    height: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: var(--selection-color);
    border-radius: 4px;
    height: 20px;
  }

  .custom-scrollbar:hover::-webkit-scrollbar-thumb {
    background-color: var(--selection-color);
  }

  .custom-scrollbar:not(:hover):not(:active)::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
</style>
